import { IonButton, IonIcon, IonModal, IonSkeletonText } from '@ionic/react'
import { PatientStatus, toStatus } from 'domain/usecases/list-patients'
import { labs } from 'helpers/labs'
import { PatientsService } from 'infra/services/patients-service'
import { ReportsService } from 'infra/services/reports-service'
import { UserInternalService } from 'infra/services/user-internal-service'
import { checkmark, pencilOutline, print, shareSocial, warning } from 'ionicons/icons'
import { route } from 'main/routes'
import { ButtonLink, ButtonRound } from 'presentation/components'
import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router'
import { useRecoilValue } from 'recoil'
import { reportAtom } from '../../report-atom'
import ModalEditReportHeader, { ReportEdition } from '../modal-edit-report-header/modal-edit-report-header'
import ModalShareReport from '../modal-share-report/modal-share-report'
import './report-header.scss'
import ModalEditPatient from '../modal-edit-patient/modal-edit-patient'
import { UserInfo, UserRole } from 'infra/services/usecases/get-user-info'
import { ReportStatus } from 'domain/usecases/get-report'
import ModalReportIssue from '../modal-report-issue/modal-report-issue'

interface ReportHeaderProps {
  patientsService: PatientsService
  userInternalService: UserInternalService
  reportsService: ReportsService
  isViewOnly: boolean
  onEditHeaderDismiss: () => void
  handlePrint?: () => void
  onApproveReport: () => void
  user: UserInfo | null
}

export const ReportHeader: React.FC<ReportHeaderProps> = (props) => {
  const { report, isLoadingReport, reportPdfLink } = useRecoilValue(reportAtom)
  const fallback = 'Unknown'
  const modalEdit = useRef<HTMLIonModalElement>(null)
  const modalEditPatient = useRef<HTMLIonModalElement>(null)
  const modalReportIssue = useRef<HTMLIonModalElement>(null)
  const [isModalEditOpen, setModalEditOpen] = useState(false)
  const [isModalEditPatientOpen, setModalEditPatientOpen] = useState(false)
  const [isModalReportIssueOpen, setModalReportIssueOpen] = useState(false)
  const [isOpenShare, setOpenShare] = useState(false)

  const navigate = useHistory()

  const Skeleton = (params: { size: number; height?: number }) => (
    <IonSkeletonText animated={true} style={{ width: params.size, height: params.height, display: 'inline-block' }} />
  )

  const onEditReport = () => setModalEditOpen(true)

  const onReportIssue = () => setModalReportIssueOpen(true)

  return (
    <section className="report-header">
      <div className="patient-line">
        <h1>{isLoadingReport ? <Skeleton size={300} height={30} /> : <b>{report?.patient?.name}</b>}</h1>

        {!props.isViewOnly && props.user && (
          <div className="actions">
            {props.user?.role === UserRole.Reviewer && report?.status !== ReportStatus.processing ? (
              <IonButton size="small" expand="block" onClick={onEditReport}>
                <IonIcon icon={pencilOutline} slot="end" />
                Edit Report
              </IonButton>
            ) : undefined}
            {props.user?.role === UserRole.Oncologist && report?.status === ReportStatus.reviewed ? (
              <IonButton size="small" expand="block" onClick={onReportIssue}>
                <IonIcon icon={warning} slot="end" />
                Report Issue
              </IonButton>
            ) : undefined}
            {!isLoadingReport ? (
              <ButtonRound color="light" size="small" onClick={props.handlePrint}>
                <IonIcon icon={print} slot="start" />
                Print
              </ButtonRound>
            ) : undefined}
            {!isLoadingReport && props.user?.role === UserRole.Oncologist ? (
              <ButtonRound color="tertiary" size="small" onClick={() => setOpenShare(true)}>
                <IonIcon icon={shareSocial} slot="start" />
                Share
              </ButtonRound>
            ) : undefined}
            {!isLoadingReport && props.user?.role === 'Reviewer' && report?.status !== ReportStatus.processing ? (
              <ButtonRound color="tertiary" size="small" onClick={props.onApproveReport}>
                <IonIcon icon={checkmark} slot="start" />
                Approve
              </ButtonRound>
            ) : undefined}
          </div>
        )}
      </div>

      <div className="report-header-content">
        <div className="left-side">
          <div className="subcontent">
            {isLoadingReport ? (
              <Skeleton size={150} />
            ) : (
              <>
                <span>{report?.patient?.sex || fallback} - </span>{' '}
                <span>{report?.patient?.age ? `${report?.patient?.age}yo` : fallback} | </span>
                <span>#{report?.patient?.MRN || fallback} | </span>
                {!props.isViewOnly && (
                  <>
                    <ButtonLink
                      onClick={() => {
                        navigate.push(`${route.patient.url}`, {
                          patientItem: report?.patient,
                        })
                      }}
                      className="patient-link"
                    >
                      See Patient Detail
                    </ButtonLink>
                  </>
                )}
              </>
            )}
          </div>

          <div className="report-header-card">
            <div className="card-item">
              <span className="label card-item-label">Cancer Type:</span>
              {isLoadingReport ? (
                <Skeleton size={150} />
              ) : (
                <b>{report?.patient?.cancer_type || report?.diagnosis || fallback}</b>
              )}
            </div>

            <div className="content">
              <div className="card-item">
                <span className="label card-item-label">Stage:</span>

                {isLoadingReport ? <Skeleton size={150} /> : <b>{report?.patient?.cancer_stage || fallback}</b>}
              </div>

              <div className="card-item">
                <span className="label card-item-label">Status:</span>
                {isLoadingReport ? (
                  <Skeleton size={150} />
                ) : (
                  <b>{report?.patient?.status ? toStatus[report?.patient?.status] : fallback}</b>
                )}
              </div>
              {(!props.isViewOnly && !isLoadingReport && props.user?.role === UserRole.Oncologist) && (
                <IonButton
                  fill="outline"
                  size="small"
                  onClick={() => setModalEditPatientOpen(true)}
                  disabled={!!isLoadingReport}
                >
                  Edit
                </IonButton>
              )}
            </div>
          </div>
        </div>

        <div className="report-header-extra-data">
          <div className="subcontent">
            <span>Lab: {isLoadingReport ? <Skeleton size={100} /> : labs[report?.lab as string] || fallback} • </span>
            {isLoadingReport ? (
              <Skeleton size={150} />
            ) : (
              <a href={reportPdfLink as string} target="blank">
                View original report
              </a>
            )}
          </div>
          {props.user?.role === UserRole.MedicalAssistant && (
            <div className="subcontent">
              Oncologist: {report?.doctor_name || fallback}
            </div>
          )}
          {
            (report?.status === ReportStatus.reviewed && (props.user?.role === UserRole.Oncologist || props.user?.role === UserRole.MedicalAssistant)) || (props.user?.role === UserRole.Reviewer) ?
              <div className="content-grid">
                <div>
                  <span className="label">SPECIMEN</span>
                  {isLoadingReport ? <Skeleton size={150} /> : report?.tumor_specimen || fallback}
                </div>

                <div>
                  <span className="label">REPORT DATE</span>
                  {isLoadingReport ? <Skeleton size={150} /> : report?.report_date || fallback}
                </div>

                <div>
                  <span className="label">SPECIMEN TYPE</span>
                  {isLoadingReport ? <Skeleton size={150} /> : report?.specimen_type || fallback}
                </div>

                <div>
                  <span className="label">BIOPSY DATE</span>
                  {isLoadingReport ? <Skeleton size={150} /> : report?.biopsy_date || fallback}
                </div>
              </div> :
              <small>
                Additional details available after report is reviewed
              </small>
          }
        </div>
      </div>
      <IonModal
        className="modal-edit-patient"
        ref={modalEditPatient}
        isOpen={isModalEditPatientOpen}
        onDidDismiss={(info) => {
          if (info.detail.data && info.detail.data.wasEdited) props.onEditHeaderDismiss()
          setModalEditPatientOpen(false)
        }}
      >
        {report?.patient && (
          <ModalEditPatient
            modal={modalEditPatient}
            patientsService={props.patientsService}
            patientEdition={{
              cancerType: report?.patient.cancer_type as string,
              cancerStage: report?.patient.cancer_stage as string,
              status: report?.patient.status as PatientStatus,
              mrn: report?.patient.MRN as string,
              priorTreatments: report?.patient.prior_treatments as string,
              payer: report?.patient.payer as string,
              comorbidities: report?.patient.co_morbidities as string,
              address: '',
              insurance_id_number: report?.patient.insurance_id_number as string,
              group_id_number: report?.patient.group_id_number as string,
              stages: report?.patient.cancer_stage as string,
            }}
            headerTitle="Edit patient information"
            fields={{
              cancerType: true,
              stage: true,
              status: true,
              priorTreatments: false,
              payer: false,
              comorbidities: false,
              insuranceId: false,
              groupId: false,
              address: false,
            }}
          />
        )}

      </IonModal>

      <IonModal
        className="modal-edit-report"
        ref={modalEdit}
        isOpen={isModalEditOpen}
        onDidDismiss={(info) => {
          if (info.detail.data && info.detail.data.wasEdited) props.onEditHeaderDismiss()
          setModalEditOpen(false)
        }}
      >

        {report ? (
          <>
            <ModalEditReportHeader
              modal={modalEdit}
              reportsService={props.reportsService}
              headerTitle="Edit report details"
              reportEdition={
                {
                  reportID: report?.report_id,
                  biopsyDate: report?.biopsy_date,
                  specimen: report?.tumor_specimen,
                  specimenType: report?.specimen_type,
                  reportDate: report?.report_date,
                  genomicVariants: report?.genomic_variants,
                  treatments: report?.treatments,
                  tmb_percentile: report?.tmb_percentile,
                  selected_treatments: report?.selected_treatments,
                  microsatellite_instability_status: report?.microsatellite_instability_status,
                  ct_dna_tumor_fraction: report?.ct_dna_tumor_fraction,
                  hrd_signature: report?.hrd_signature,
                  tumor_mutational_burden: report?.tumor_mutational_burden,
                } as ReportEdition
              }
              fields={{
                reportID: true,
                biopsyDate: true,
                specimen: true,
                specimenType: true,
                reportDate: true,
                genomicVariants: true,
                treatments: true
              }}
            />
          </>
        ) : undefined}
      </IonModal>

      <IonModal isOpen={isOpenShare} onDidDismiss={() => setOpenShare(false)}>
        <ModalShareReport
          dismiss={() => setOpenShare(false)}
          usersService={props.userInternalService}
          reportsService={props.reportsService}
          reportId={report?.report_id as string}
        />
      </IonModal>
      <IonModal className="modal-report-issue" ref={modalReportIssue} isOpen={isModalReportIssueOpen} onDidDismiss={(info) => {
        if (info.detail.data && info.detail.data.wasEdited) {
          navigate.push(route.dashboard.url)
        }
        setModalReportIssueOpen(false)
      }}>
        {report ? (
          <ModalReportIssue
            modal={modalReportIssue}
            reportsService={props.reportsService}
            reportEdition={{
              reportID: report?.report_id,
              biopsyDate: report?.biopsy_date,
              specimen: report?.tumor_specimen,
              specimenType: report?.specimen_type,
              reportDate: report?.report_date,
              genomicVariants: report?.genomic_variants,
              treatments: report?.treatments,
              tmb_percentile: report?.tmb_percentile,
              selected_treatments: report?.selected_treatments,
              microsatellite_instability_status: report?.microsatellite_instability_status,
              ct_dna_tumor_fraction: report?.ct_dna_tumor_fraction,
              hrd_signature: report?.hrd_signature,
              tumor_mutational_burden: report?.tumor_mutational_burden,
            } as ReportEdition}
            headerTitle="Report Issue"
          />
        ) : undefined}
      </IonModal>


    </section>
  )
}
